import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';

import { PaymentStatus } from '@fcom/common-booking/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { CartState, CartStatus, DapiErrorStatus } from '@fcom/dapi';
import { unsubscribe } from '@fcom/core/utils';
import { FinnairCart, FinnairOrder } from '@fcom/dapi/api/models';
import { orderData, orderResponseErrorStatus, OrderActions } from '@fcom/common-booking/store';
import { PaymentActions, BookingOrderService } from '@fcom/common-booking';
import { Step } from '@fcom/common/interfaces';
import { finShare } from '@fcom/rx';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { GlobalBookingActions } from '@fcom/common/store';
import { ExpanderStatus } from '@fcom/booking-widget/interfaces';

import { CartActions } from '../../../store/actions';
import { cartState } from '../../../store/selectors';
import { BookingService } from '../../../services';
import { navigateToError } from '../../../utils/route-utils';

@Component({
  selector: 'fin-booking-purchase-review-route',
  templateUrl: './booking-purchase-review-route.component.html',
})
export class BookingPurchaseReviewRouteComponent implements OnInit, OnDestroy {
  @HostBinding('class') classAttribute = 'grid-col-12 pr-clear';
  readonly cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  readonly cartOrOrderStatus$: Observable<CartStatus>;

  private subscription: Subscription;

  constructor(
    private bookingService: BookingService,
    private store$: Store<BookingAppState>,
    private activatedRoute: ActivatedRoute,
    private bookingOrderService: BookingOrderService,
    private router: Router,
    private languageService: LanguageService
  ) {
    const cartState$: Observable<CartState> = this.store$.pipe(cartState(), finShare());

    const order$: Observable<FinnairOrder> = this.store$.pipe(orderData(), startWith<FinnairOrder>(null), finShare());
    this.cartOrOrder$ = order$.pipe(
      switchMap((order) => {
        return order ? of(order) : cartState$.pipe(map((state) => state.cartData));
      })
    );

    this.cartOrOrderStatus$ = order$.pipe(
      switchMap((order) => {
        return order ? of(CartStatus.OK) : cartState$.pipe(map((state) => state.cartStatus));
      })
    );
  }

  ngOnInit(): void {
    this.subscription = new Subscription();
    this.checkIfReviewAndPayRequestedAndHandleLoadingErrorAndIfAlreadyPaid();
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  proceedToPayment(): void {
    this.store$.dispatch(CartActions.acceptTerms());
    this.bookingService.nextStep();
  }

  setActive({ next, openGlobalBookingWidget = false }: { next: Step; openGlobalBookingWidget?: boolean }): void {
    if (!next) {
      return;
    }
    if (next.id !== this.bookingService.currentStep.id) {
      this.bookingService.setStep({
        step: next,
      });

      if (openGlobalBookingWidget) {
        this.bookingService.setGlobalBookingWidgetExpanderStatus(ExpanderStatus.OPEN);
      }
    }
  }

  private checkIfReviewAndPayRequestedAndHandleLoadingErrorAndIfAlreadyPaid(): void {
    const requestedOrderId = this.activatedRoute.snapshot.queryParamMap.get('orderid');
    const requestedHash = this.activatedRoute.snapshot.queryParamMap.get('hash');

    if (requestedOrderId && requestedHash) {
      this.store$.dispatch(
        OrderActions.setOrderFetchInformation({ orderInformation: { id: requestedOrderId, hash: requestedHash } })
      );

      this.bookingOrderService.queueOrderLoad();

      // We show different error page depending on which response error status we get
      const orderResponseErrorStatus$ = this.store$.pipe(orderResponseErrorStatus());

      this.subscription.add(
        orderResponseErrorStatus$.subscribe((status) => {
          switch (status) {
            case DapiErrorStatus.INVALID_INPUT:
              navigateToError(this.router, this.languageService.langValue, 'REVIEW_AND_PAY_ORDER_FETCH_INVALID_INPUT', {
                info: 'errors.reviewAndPay.orderFetch.invalidInput.description',
                showButton: false,
              });
              break;
            case DapiErrorStatus.OTHER_ERROR:
              navigateToError(this.router, this.languageService.langValue, 'REVIEW_AND_PAY_ORDER_FETCH_OTHER_ERROR', {
                info: 'errors.reviewAndPay.orderFetch.otherError.description',
                showButton: false,
              });
              break;
            default:
              navigateToError(this.router, this.languageService.langValue, 'REVIEW_AND_PAY_ORDER_FETCH_HTTP_ERROR', {
                info: 'errors.reviewAndPay.orderFetch.httpError.description',
                showButton: false,
              });
              break;
          }
        })
      );

      this.isPNRAlreadyPaid();
    }
  }

  private isPNRAlreadyPaid(): void {
    this.subscription.add(
      this.cartOrOrder$.pipe(filter((cartOrOrder) => !!cartOrOrder)).subscribe((order: FinnairOrder) => {
        if (!parseFloat(order.prices.unpaid.total.total.totalAmount.amount)) {
          this.store$.dispatch(PaymentActions.setPaymentStatus({ status: PaymentStatus.VERIFIED }));
          this.store$.dispatch(GlobalBookingActions.paymentVerified());

          this.router.navigate([`../${BOOKING_STEPS.PURCHASE_SUCCESS.path}`], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge',
            queryParams: { reviewAndPayRedirect: true },
          });
        }
      })
    );
  }
}
